import {useCallback, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {SegmentedControl} from '@tickchak/react-share/app';
import {orderChangePhoneDirect, orderChangePhoneSend, orderChangePhoneVerify} from '../../../../../redux/crm/crmActions';
import setGlobalState from '../../../../../redux/setGlobalState';
import {bounceMessage} from '../../../../../layout/userMessages/UserMessage';
import {TState} from '../../../../../redux/store.type';
import {updateExpiredTime} from '../../../../../component/SMSCode/SMSCode';
import {parsePhoneForOrder, PhoneFormValues, PhoneChangeMode, Step} from '../../utils';
import {reloadIframe} from '../../../../../layout/myIframe/MyIframe';
import LockOutline from '../../../../../assets/icons/LockOutline';
import PhoneHandset from '../../../../../assets/icons/PhoneHandset';
import {BLACK} from '../../../../../globalVariables/GlobalVariables';
import * as css from './PhoneWizard.css';
import PhoneFormStep from '../PhoneFormStep/PhoneFormStep';
import CodeFormStep from '../CodeFormStep/CodeFormStep';
import SuccessFormStep from '../SuccessFormStep/SuccessFormStep';
import PrimaryActionButton from '../PrimaryActionButton/PrimaryActionButton';

type Props = {
    orderData: NonNullable<TState['crm']['orderChangePhone']>;
    onClose: () => void;
};

function PhoneWizard({orderData, onClose}: Props) {
    const {t} = useTranslation('layout');
    const {sendXHR, verifyXHR, directXHR, codeInput} = useSelector((s: TState) => ({
        sendXHR: s.crm.orderChangePhoneSendXHR,
        verifyXHR: s.crm.orderChangePhoneVerifyXHR,
        directXHR: s.crm.orderChangePhoneDirectXHR,
        codeInput: s.ui.codeInput?.code ?? '',
    }));
    const [step, setStep] = useState<Step>('phone');
    const [newPhoneValues, setNewPhoneValues] = useState<PhoneFormValues | null>(null);
    const [phoneChangeMode, setPhoneChangeMode] = useState<PhoneChangeMode>('secure');

    const phoneModeSegments = useMemo(
        () =>
            [
                {
                    id: 'regular' as const,
                    label: t('orderChangePhoneTabRegular'),
                    content: <PhoneHandset size={20} />,
                },
                {
                    id: 'secure' as const,
                    label: t('orderChangePhoneTabSecure'),
                    content: <LockOutline width={20} height={20} fill={BLACK} />,
                },
            ] as const,
        [t],
    );

    const handlePhoneModeChange = useCallback((value: string) => {
        setPhoneChangeMode(value as PhoneChangeMode);
    }, []);

    const handleSendCode = async (phoneInputValues: PhoneFormValues) => {
        const phoneForApi = parsePhoneForOrder(phoneInputValues.phone, phoneInputValues.country);
        if(!phoneForApi) return;
        if(phoneChangeMode === 'secure') {
            const res = await orderChangePhoneSend(orderData.oid, phoneForApi);
            if(!res) {
                bounceMessage(t('orderChangePhoneSendFailed'), 'warning');
                return;
            }
            setStep('code');
            updateExpiredTime();
        } else {
            const res = await orderChangePhoneDirect(orderData.oid, phoneForApi);
            if(res) {
                setStep('success');
                reloadIframe();
            }
        }
        setNewPhoneValues({...phoneInputValues});
    };

    const handleVerifyCode = useCallback(async (codeDigits: string) => {
        if(verifyXHR === 'LOADING') return;
        const trimmed = codeDigits.replace(/\D/g, '');
        if(trimmed.length < 6) {
            setGlobalState('ui.codeInput.isWrong', true);
            return;
        }
        if(!newPhoneValues) return;
        const phoneForVerify = parsePhoneForOrder(newPhoneValues.phone, newPhoneValues.country);
        if(!phoneForVerify) {
            bounceMessage(t('orderChangePhoneInvalidPhone'), 'warning');
            return;
        }
        const res = await orderChangePhoneVerify(orderData.oid, trimmed, phoneForVerify);
        if(res) {
            setStep('success');
            reloadIframe();
        }
    }, [orderData, newPhoneValues, verifyXHR, t]);

    const canShowCodeStep = step === 'code' && phoneChangeMode === 'secure' && newPhoneValues;

    return (
        <>
            <css.formBlock>
                <css.stepSegmentsWrap $isSuccess={step === 'success'}>
                    <SegmentedControl
                        segments={phoneModeSegments}
                        value={phoneChangeMode}
                        onChange={step === 'phone' ? handlePhoneModeChange : undefined}
                        groupLabel={t('orderChangePhoneModeGroupLabel')}
                        data-cy="orderChangePhoneModeSegments"
                    />
                </css.stepSegmentsWrap>
                {step === 'phone' && (
                    <PhoneFormStep
                        orderData={orderData}
                        phoneChangeMode={phoneChangeMode}
                        onSubmit={handleSendCode}
                    />
                )}
                {canShowCodeStep && (
                    <CodeFormStep
                        orderData={orderData}
                        newPhone={newPhoneValues}
                        sendXHR={sendXHR}
                        onVerifyCode={handleVerifyCode}
                        onSendCode={handleSendCode}
                    />
                )}
                {step === 'success' && newPhoneValues && (
                    <SuccessFormStep orderData={orderData} newPhoneValues={newPhoneValues} />
                )}
            </css.formBlock>
            <css.footer>
                <PrimaryActionButton
                    step={step}
                    onClose={onClose}
                    onVerifyCode={handleVerifyCode}
                    verifyXHR={verifyXHR}
                    directXHR={directXHR}
                    sendXHR={sendXHR}
                    phoneChangeMode={phoneChangeMode}
                    codeFromStore={codeInput}
                    disabled={codeInput.length !== 6 && step === 'code'}
                />
            </css.footer>
        </>
    );
}

export default PhoneWizard;
